import { useTranslation } from "react-i18next";
import {
  Avatar,
  Dropdown,
  Form,
  Layout,
  MenuProps,
  message,
  Modal,
  TableColumnsType,
  TableProps,
  Typography,
} from "antd";
import FilterTable from "./component/filter-table/FilterTable";
import TableData from "../../../../../components/table-data/TableData";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { DataType } from "./component/constants/data";
import {
  formatDateToIsoStringHHmm,
  formatDateToIsoStringHHss,
} from "../../../../../utils/converDate";
import { isEmpty } from "lodash";
import {
  deleteQuizInQuestionBank,
  duplicateQuizToQuestionBank,
  getQuizVersionDetailsInQuestionBank,
} from "../../../../../service/library";
import PageHeader from "../../../../../components/page-header/PageHeader";
import QuizPreview from "../../../../../components/quiz-preview/QuizPreview";
import { useAddLesson } from "../../../../../stores/stores";
import { ModeQuestion } from "../../../../../constants";
import { convertLatexToImage } from "../../../../../utils/app";

type ListVersionsQuizProps = {
  data?: any;
};

const ListVersionsQuiz = ({ data }: ListVersionsQuizProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setModeQuestion } = useAddLesson();
  const params = new URLSearchParams(location.search);
  const name = params.get("name");
  const permission = params.get("permission");
  const quizId = params.get("quizId");
  const [dataQuiz, setDataQuiz] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusOrder, setStatusOrder] = useState<string>("");
  const [fieldSortOrder, setFieldSortOrder] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [quizDetailId, setQuizDetailId] = useState<number>(0);
  const [showUsedTable, setShowUsedTable] = useState<boolean>(false);

  const [isOpenQuizPreviewModal, setIsOpenQuizPreviewModal] =
    useState<boolean>(false);

  const handleGoToVersionEdit = (item: any) => {
    if (!item) return;
    setModeQuestion(ModeQuestion.UPDATE);
    const basePath = location.pathname.split("/versions")[0];
    navigate(
      `${basePath}/add-questions?name=${name}&quizId=${item.id}&version=${item.questionBankVersion}`
    );
  };

  // quiz dropdown action
  const getMenuItems = (item: any): MenuProps["items"] => [
    {
      key: "1",
      label: (
        <div
          onClick={() => {
            setShowUsedTable(false);
            setQuizDetailId(item.id);
            setIsOpenQuizPreviewModal(true);
          }}
        >
          <EyeOutlined />
          {t("&nbsp; Xem trước", "&nbsp; Xem trước")}
        </div>
      ),
    },
    ...(permission === "edit"
      ? [
          {
            key: "2",
            label: (
              <div onClick={() => handleGoToVersionEdit(item)}>
                <EditOutlined /> {t("Chỉnh sửa", "Chỉnh sửa")}{" "}
              </div>
            ),
          },
        ]
      : []),
    ...(permission === "edit"
      ? [
          {
            key: "3",
            label: (
              <div onClick={() => duplicateQuiz(item.id)}>
                <CopyOutlined />
                {t("&nbsp; Tạo bản sao", "&nbsp; Tạo bản sao")}
              </div>
            ),
          },
        ]
      : []),
    ...(permission === "edit"
      ? [
          {
            key: "4",
            label: (
              <div
                style={{ color: "#ff4d4f" }}
                onClick={() =>
                  Modal.confirm({
                    title: `Xóa câu hỏi`,
                    content: `Dữ liệu đã xoá sẽ không thể khôi phục. Bạn có chắc chắn muốn xoá?`,
                    onOk: () => handleDeleteQuiz(item.id),
                    centered: true,
                    okText: t("Xác nhận", "Xác nhận"),
                    cancelText: t("Huỷ bỏ", "Huỷ bỏ"),
                    footer: (_, { OkBtn, CancelBtn }) => (
                      <>
                        <CancelBtn />
                        <OkBtn />
                      </>
                    ),
                  })
                }
              >
                <DeleteOutlined />
                {t("&nbsp; Xóa", "&nbsp; Xóa")}
              </div>
            ),
          },
        ]
      : []),
  ];

  const dataColumns: TableColumnsType<DataType> = [
    {
      title: "STT",
      dataIndex: "numericalOrder",
      key: "numericalOrder",
      width: 5,
      render: (_: any, record, index: number) => {
        return <div>{record?.numericalOrder}</div>;
      },
    },
    {
      title: t("Câu hỏi", "Câu hỏi"),
      dataIndex: "questionValue",
      key: "questionValue",
      className: "question-col",
      render: (_: any, record, index: number) => {
        return (
          <div
            className="question-content"
            dangerouslySetInnerHTML={{
              __html: `${convertLatexToImage(record?.question)}`,
            }}
          ></div>
        );
      },
      width: 50,
    },
    {
      title: t("Phiên bản", "Phiên bản"),
      dataIndex: "questionBankVersion",
      key: "questionBankVersion",
      width: 10,
    },
    {
      title: t("Lượt sử dụng", "Lượt sử dụng"),
      dataIndex: "numberOfUses",
      key: "numberOfUses",
      render: (_: any, record: any) => {
        return (
          <Typography.Link
            onClick={() =>
              navigate(
                `${location.pathname.replace(
                  /\/versions$/,
                  ""
                )}/used?name=${name}&quizId=${record.id}&version=${
                  record.questionBankVersion
                }`
              )
            }
          >
            {record.numberOfUses}
          </Typography.Link>
        );
      },
      width: 15,
    },
    {
      title: t("Chủ sở hữu", "Chủ sở hữu"),
      dataIndex: "author",
      key: "author",
      render: (_: any, record: any) => {
        return (
          <div className="quiz-author">
            <div>
              <Avatar src={record.author?.avatar} />
            </div>
            <div className="quiz-author__info">
              <span className="quiz-author__info-email">
                {record.author?.email}
              </span>
              <span>
                {record.createdDate
                  ? formatDateToIsoStringHHmm(record.createdDate)
                  : null}
              </span>
            </div>
          </div>
        );
      },
      width: 30,
      className: "author-col",
    },
    {
      title: t("Chỉnh sửa lần cuối", "Chỉnh sửa lần cuối"),
      dataIndex: "editor",
      key: "editor",
      render: (_: any, record: any) => {
        return (
          <div className="quiz-editor">
            <div>
              <Avatar
                src={
                  record.editor ? record.editor?.avatar : record.author?.avatar
                }
              />
            </div>
            <div className="quiz-editor__info">
              <span className="quiz-editor__info-email">
                {record.editor ? record.editor?.email : record.author?.email}
              </span>
              <span>
                {record.updatedDate
                  ? formatDateToIsoStringHHmm(record.updatedDate)
                  : formatDateToIsoStringHHmm(record.createdDate)}
              </span>
            </div>
          </div>
        );
      },
      width: 30,
      className: "editor-col",
    },
    {
      title: "",
      dataIndex: "action",
      key: "operation",
      render: (_, record) => {
        return (
          <Dropdown
            menu={{ items: getMenuItems(record) }}
            placement="bottomRight"
          >
            <span
              style={{ display: "inline-block", padding: 4, cursor: "pointer" }}
            >
              <MoreOutlined style={{ fontSize: 18 }} />
            </span>
          </Dropdown>
        );
      },
      fixed: "right",
      width: 10,
    },
  ];

  // Sort
  const handleTableChange: TableProps["onChange"] = (
    pagination,
    filters,
    sorter: any
  ) => {
    const data =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : "";
    setStatusOrder(data);
    setFieldSortOrder(sorter.field);
  };

  // fetch data
  const fetchData = async () => {
    const dataReq = {
      bankId: id,
      quizId: quizId,
      keyWord: "",
      size: pageSize,
      page: pageNumber,
      sort:
        isEmpty(fieldSortOrder) || isEmpty(statusOrder)
          ? ["updated_date, desc"]
          : [`${fieldSortOrder},${statusOrder}`],
    };

    try {
      setLoading(true);
      const res = await getQuizVersionDetailsInQuestionBank(dataReq);
      if (res.status === 200) {
        setDataQuiz(res.data?.data?.content);
        setTotalRecords(res.data?.data?.totalElements);
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // handle duplicate unit bank
  const duplicateQuiz = async (quizId: number) => {
    setLoading(true);
    try {
      const res = await duplicateQuizToQuestionBank(quizId);
      if (res.status === 200) {
        setLoading(false);
        message.success(t("Tạo bản sao thành công", "Tạo bản sao thành công"));
        fetchData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // handle delete quiz
  const handleDeleteQuiz = async (quizId: number) => {
    try {
      const res = await deleteQuizInQuestionBank(quizId);
      if (res.status === 200) {
        message.success(t("Xóa thành công", "Xóa thành công"));
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  // useEffect
  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, pageNumber, pageSize]);

  return (
    <Layout className="page-header-group unit-list-page">
      <Form.Provider>
        <div className={`page-header-group--layout`}>
          <div className="table-header--wrapper">
            <div className="table-header--left">
              <PageHeader
                title={`Ngân hàng câu hỏi/ ${name}/ Danh sách phiên bản`}
              />
            </div>
            {/* <FilterTable /> */}
          </div>
        </div>
        <TableData
          dataTable={dataQuiz}
          dataColumns={dataColumns}
          setPageNumber={setPageNumber}
          setPageSize={setPageSize}
          pageNumber={pageNumber}
          pageSize={pageSize}
          pagination={true}
          scroll={{ x: 1500, y: 600 }}
          totalRecords={totalRecords}
          loadingTable={loading}
          handleTableChangeProps={handleTableChange}
        />
      </Form.Provider>
      <QuizPreview
        fullHeight
        hiddenVersionSelect
        quizId={quizDetailId}
        showUsedTable={showUsedTable}
        isOpenModal={isOpenQuizPreviewModal}
        setQuizDetailId={setQuizDetailId}
        setIsOpenModal={setIsOpenQuizPreviewModal}
      />
    </Layout>
  );
};

export default ListVersionsQuiz;
